import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardLayout from "@/components/DashboardLayout";
import { Empty, ApplicationPipeline, StatusBadge } from "@/components/Shared";
import { api, formatApiError } from "@/lib/api";
import { payProgramFee } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

export default function Applications() {
    const { user } = useAuth();
    const [apps, setApps] = useState(null);
    const [payingId, setPayingId] = useState(null);

    const load = () => api.get("/applications").then((r) => setApps(r.data)).catch(() => setApps([]));
    useEffect(() => { load(); }, []);

    const pay = async (app) => {
        setPayingId(app.id);
        try {
            const result = await payProgramFee(app, user);
            if (result.ok) {
                toast.success("Payment confirmed!");
                load();
            } else if (result.message) {
                toast.info(result.message);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPayingId(null);
        }
    };

    return (
        <DashboardLayout title="My Applications">
            <div className="mx-auto max-w-4xl" data-testid="applications-page">
                <h1 className="font-display text-2xl font-bold tracking-tight">My Applications</h1>
                <p className="mt-1 text-sm text-muted-foreground">Track every application from Submitted to Selected — you'll be notified at each change.</p>
                {apps === null ? <p className="mt-8 text-sm text-muted-foreground">Loading...</p>
                : apps.length === 0 ? <div className="mt-8"><Empty icon={FileText} title="No applications yet" hint="Head to the Internship Hub and find something worth applying for." /></div>
                : (
                    <div className="mt-8 space-y-5">
                        {apps.map((a) => (
                            <div key={a.id} className="card-soft p-6" data-testid={`application-${a.id}`}>
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <p className="font-display text-lg font-semibold">{a.internship_title}</p>
                                        <p className="mt-0.5 text-xs text-muted-foreground">{a.duration_months} Months · ${a.fee_usd} program fee · applied {new Date(a.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <StatusBadge status={a.payment_status === "paid" ? "paid" : "pending"} />
                                    </div>
                                </div>
                                <div className="mt-5"><ApplicationPipeline status={a.status} /></div>
                                {a.payment_status !== "paid" && (
                                    <div className="mt-5 flex items-center gap-4 rounded-xl bg-muted p-4">
                                        <p className="flex-1 text-xs text-muted-foreground">Program fee payment is pending. Complete it to finalize your application.</p>
                                        <Button data-testid={`pay-button-${a.id}`} size="sm" disabled={payingId === a.id} className="rounded-full" onClick={() => pay(a)}>
                                            <IndianRupee className="mr-1 h-3.5 w-3.5" />{payingId === a.id ? "Processing..." : "Pay Fee"}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}
